import { useEffect, useState } from "react";
import { History, RotateCcw, Loader2, GitCompare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VersionDiff } from "@/components/VersionDiff";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function VersionHistoryDialog({ automationId, open, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [diff, setDiff] = useState(null);      // {versionId, against}
  const [picked, setPicked] = useState([]);    // id versioni selezionate per il confronto a due
  useEffect(() => {
    if (!open || !automationId) return;
    setRows(null); setDiff(null); setPicked([]);
    api.get(`/automations/${automationId}/versions`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [open, automationId]);
  const restore = async (v) => {
    setBusy(v.id);
    try {
      const { data } = await api.post(`/automations/${automationId}/versions/${v.id}/restore`);
      toast.success(`Versione ${v.version_no} ripristinata`);
      onRestored?.(data);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Ripristino non riuscito"); }
    finally { setBusy(""); }
  };
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p.slice(-1), id]));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-xl z-[80] [&~div]:z-[70]" data-testid="version-history-dialog">
        <DialogHeader><DialogTitle className="text-slate-900 text-lg flex items-center gap-2"><History className="h-4 w-4 text-[#5BC65F]" /> {diff ? (diff.restore ? "Anteprima ripristino" : "Confronto versioni") : "Cronologia modifiche"}</DialogTitle></DialogHeader>
        {diff ? (
          <>
            {diff.restore && <p className="text-xs text-slate-500 -mt-2" data-testid="restore-preview-hint">Controlla le differenze rispetto alla versione attuale, poi conferma il ripristino in fondo. La versione attuale verrà salvata nella cronologia.</p>}
            <VersionDiff automationId={automationId} versionId={diff.versionId} against={diff.against} busy={!!busy}
                         onBack={() => setDiff(null)} onRestore={(from) => restore({ id: from.id, version_no: from.version_no, saved_at: from.saved_at })} />
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500 -mt-2">Ogni salvataggio conserva la versione precedente (ultime 20). «Ripristina» mostra prima le differenze e chiede conferma; spunta due versioni per confrontarle tra loro.</p>
            {!rows ? <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div> : !rows.length ? (
              <p className="text-sm text-slate-500 py-6 text-center" data-testid="version-history-empty">Nessuna versione precedente: la cronologia parte dal prossimo salvataggio.</p>
            ) : (
              <>
                {picked.length === 2 && (
                  <div className="flex items-center justify-between rounded-lg bg-[#5BC65F]/10 border border-[#5BC65F]/40 px-3 py-1.5" data-testid="version-compare-bar">
                    <span className="text-xs text-slate-700">2 versioni selezionate</span>
                    <Button size="sm" onClick={() => setDiff({ versionId: picked[0], against: picked[1] })} data-testid="version-compare-picked" className="h-7 text-white" style={{ backgroundColor: "#2F7D32" }}>
                      <GitCompare className="h-3.5 w-3.5 mr-1" /> Confronta selezionate
                    </Button>
                  </div>
                )}
                <div className="max-h-[55vh] overflow-y-auto space-y-1.5" data-testid="version-history-list">
                  {rows.map((v) => (
                    <div key={v.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${picked.includes(v.id) ? "border-[#5BC65F] bg-[#5BC65F]/5" : "border-slate-200"}`} data-testid={`version-row-${v.version_no}`}>
                      <input type="checkbox" checked={picked.includes(v.id)} onChange={() => togglePick(v.id)} data-testid={`version-pick-${v.version_no}`}
                             className="h-4 w-4 accent-[#2F7D32] cursor-pointer" title="Seleziona per confrontare due versioni" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 font-medium">Versione {v.version_no} <span className="text-slate-400 font-normal">· {fmt(v.saved_at)}</span></p>
                        <p className="text-xs text-slate-500 truncate">{v.summary}{v.by ? ` · salvata da ${v.by}` : ""}{v.reason && v.reason !== "modifica" ? ` · ${v.reason}` : ""}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => setDiff({ versionId: v.id, against: "current" })} data-testid={`version-compare-${v.version_no}`} className="h-8 px-2 text-slate-600 hover:text-[#2F7D32]" title="Confronta con la versione attuale">
                        <GitCompare className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Confronta</span>
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setDiff({ versionId: v.id, against: "current", restore: true })} disabled={!!busy} data-testid={`version-restore-${v.version_no}`} className="h-8 px-2 bg-white border-slate-200 text-slate-700 hover:text-[#2F7D32]">
                        {busy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 sm:mr-1" />}<span className="hidden sm:inline">Ripristina</span>
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
